import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, X } from 'lucide-react';
import { Product } from '../types/cart.types';
import { useCoreContext } from '../../../lib/coreContext';
import type { UpcEligibility } from '../../bottleReturns/types/bottleReturns.types';
import { cartApi } from '../api/cart.api';
import { productsApi } from '../../products/api/products.api';
import {
  CART_MAX_QUANTITY_PER_ITEM,
  cartLineId,
  isBeverageProduct,
  normalizeTemperaturePreference,
  stripCartLineTempSuffix,
  type TemperaturePreference
} from '../utils/cartNormalization';
import { useBodyScrollLock } from '../../../lib/useBodyScrollLock';
import { useFocusTrap } from '../../../lib/useFocusTrap';
import CartLineList, { type CartLineItem } from './CartLineList';
import CartReturnsSection from './CartReturnsSection';
import CartDrawerFooter from './CartDrawerFooter';
import FreeDeliveryProgressBar from './FreeDeliveryProgressBar';
import BuyAgainCarousel from '../../orders/components/BuyAgainCarousel';
import { readUncountedBagAttached } from '../../bottleReturns/components/CustomerReturnScanner';

interface ReturnUpcEntry {
  upc: string;
  quantity: number;
}

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const core = useCoreContext();
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [quotedPrices, setQuotedPrices] = useState<Record<string, number>>({});
  const [initialReturnUpcs, setInitialReturnUpcs] = useState<ReturnUpcEntry[]>([]);
  const [initialEligibilityCache, setInitialEligibilityCache] = useState<Record<string, UpcEligibility>>({});
  const [bottleReturnsCount, setBottleReturnsCount] = useState(0);
  const [bottleReturnsCredit, setBottleReturnsCredit] = useState(0);
  const [uncountedBagAttached, setUncountedBagAttached] = useState(() => readUncountedBagAttached());
  const tempQuoteSeqByProductRef = React.useRef<Record<string, number>>({});
  const drawerOpenQuoteSeqRef = React.useRef(0);
  const drawerRef = useRef<HTMLDivElement | null>(null);

  const currentUserId = core.currentUser?.id ?? core.currentUser?._id ?? 'guest';
  const isAuthenticated = Boolean(currentUserId && currentUserId !== 'guest');

  useBodyScrollLock(isOpen);
  useFocusTrap(isOpen, drawerRef, onClose);

  // Closing the cart must collapse returns + stop camera; reopen shows Open/Start, not live camera.
  useEffect(() => {
    if (!isOpen) {
      setIsScannerOpen(false);
    } else {
      setUncountedBagAttached(readUncountedBagAttached());
    }
  }, [isOpen]);

  const [returnSyncError, setReturnSyncError] = useState<string | null>(null);

  const persistReturnUpcs = useCallback(async (payload: {
    upcs: ReturnUpcEntry[];
    eligibilityCache: Record<string, UpcEligibility>;
    reason: 'hydrate' | 'add' | 'increment' | 'decrement' | 'remove' | 'clear';
  }) => {
    if (!isAuthenticated || payload.reason === 'hydrate') return;
    try {
      if (payload.upcs.length === 0 || payload.reason === 'clear') {
        await cartApi.clearReturnUpcs();
      } else {
        await cartApi.putReturnUpcs({
          upcs: payload.upcs,
          eligibilityCache: payload.eligibilityCache
        });
      }
      setReturnSyncError(null);
    } catch {
      setReturnSyncError('Could not save bottle returns to your account. Check your connection and try again before checkout.');
      core.addToast('Bottle returns did not sync to the server. Retry before checkout.', 'warning');
    }
  }, [core, isAuthenticated]);

  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await cartApi.getReturnUpcs();
        if (cancelled) return;
        const serverUpcs = Array.isArray(data?.upcs)
          ? data.upcs
              .map((entry) => {
                const upc = String(entry?.upc || '').replace(/\s+/g, '').trim();
                const quantity = Number(entry?.quantity);
                if (!/^\d{8,14}$/.test(upc) || !Number.isFinite(quantity) || quantity <= 0) return null;
                return { upc, quantity: Math.floor(quantity) };
              })
              .filter((entry): entry is ReturnUpcEntry => Boolean(entry))
          : [];
        setInitialReturnUpcs(serverUpcs);
        setInitialEligibilityCache((data?.eligibilityCache || {}) as Record<string, UpcEligibility>);
      } catch {
        // Keep local-only scanner hydrate when server cart returns are unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isOpen]);

  const suggestedProducts = useMemo(() => {
    const inCartIds = new Set<string>();
    for (const c of core.cart || []) {
      if (c.id) {
        inCartIds.add(String(c.id).trim());
        inCartIds.add(stripCartLineTempSuffix(String(c.id)).trim());
      }
      if (c.productId) inCartIds.add(String(c.productId).trim());
    }

    const isProductInCart = (p: Product): boolean => {
      const pId = String(p.id || '').trim();
      const pFid = String(p.frontendId || '').trim();
      const pSku = String(p.sku || '').trim().toUpperCase();
      const pMongo = String(p._id || '').trim();
      return Boolean(
        (pId && inCartIds.has(pId)) ||
        (pFid && inCartIds.has(pFid)) ||
        (pSku && inCartIds.has(pSku)) ||
        (pMongo && inCartIds.has(pMongo))
      );
    };

    const allProducts = (core.products || []).filter(p => {
      return (p.stock ?? 0) > 0 && !isProductInCart(p);
    }).sort((a, b) => ((b.availableForImmediateAdd !== false || b.availableForScheduledAdd !== false) ? 1 : 0) - ((a.availableForImmediateAdd !== false || a.availableForScheduledAdd !== false) ? 1 : 0));
    
    if (!allProducts.length) return [];

    const favKeywords = ['faygo', 'better made', 'vernors', 'doritos', 'red bull', 'water', 'chips'];
    const matched: Product[] = [];

    for (const kw of favKeywords) {
      const found = allProducts.find(
        p =>
          (p.name?.toLowerCase().includes(kw) || p.brand?.toLowerCase().includes(kw)) &&
          !matched.some(m => (m.id || m.frontendId || m._id) === (p.id || p.frontendId || p._id))
      );
      if (found) matched.push(found);
      if (matched.length >= 3) break;
    }

    if (matched.length < 3) {
      for (const p of allProducts) {
        if (!matched.some(m => (m.id || m.frontendId || m._id) === (p.id || p.frontendId || p._id))) {
          matched.push(p);
          if (matched.length >= 3) break;
        }
      }
    }

    return matched;
  }, [core.cart, core.products]);

  const handleAddSuggestion = useCallback((prod: Product) => {
    const prodId = prod.frontendId || prod.id || (prod as { _id?: string })._id;
    if (!prodId) return;
    const stock = prod.stock ?? 0;
    if (stock <= 0) {
      core.addToast('OUT OF STOCK', 'warning');
      return;
    }
    if (prod.availableForImmediateAdd === false && prod.availableForScheduledAdd === false) {
      core.addToast('ITEM CURRENTLY UNAVAILABLE', 'warning');
      return;
    }
    const isBeverage = isBeverageProduct(prod);
    const pref: TemperaturePreference = isBeverage && prod.canBeAmbient === false && prod.canBeChilled !== false
      ? 'chilled'
      : 'ambient';
    const lineId = cartLineId(String(prodId), pref);

    core.setCart(prev => {
      const productQty = prev
        .filter((item) => {
          const pid = stripCartLineTempSuffix(String((item as { productId?: string }).productId || item.id || '').trim());
          return pid === String(prodId);
        })
        .reduce((sum, item) => sum + Math.max(0, Number(item.quantity || 0)), 0);
      if (productQty >= stock) {
        core.addToast(`MAX STOCK REACHED (${stock})`, 'warning');
        return prev;
      }

      const existingIdx = prev.findIndex((item) => String(item.id || '').trim() === lineId);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = {
          ...next[existingIdx],
          id: lineId,
          productId: String(prodId),
          quantity: Math.min(CART_MAX_QUANTITY_PER_ITEM, next[existingIdx].quantity + 1),
          temperaturePreference: pref
        };
        return next;
      }
      return [
        ...prev,
        {
          id: lineId,
          productId: String(prodId),
          sku: prod.sku,
          frontendId: prod.frontendId,
          quantity: 1,
          temperaturePreference: pref
        }
      ];
    });
    core.addToast('ADDED TO CART', 'success');
  }, [core]);

  const lineItems = useMemo(() => {
    return (core.cart || [])
      .map(ci => {
        const preference = normalizeTemperaturePreference(ci.temperaturePreference);
        const catalogProductId = stripCartLineTempSuffix(
          String((ci as { productId?: string }).productId || ci.id || '').trim()
        );
        const lineId = cartLineId(catalogProductId, preference);
        const p = (core.products || []).find(
          prod =>
            prod.frontendId === catalogProductId ||
            prod.id === catalogProductId ||
            (prod as { _id?: string })._id === catalogProductId ||
            (prod.sku && prod.sku.toUpperCase() === catalogProductId.toUpperCase())
        );
        const catalogResolving =
          Boolean(core.isBootstrapping) ||
          (Boolean(core.productsFetching) && (core.products || []).length === 0);
        if (!p) {
          if (!catalogProductId) return null;
          const fallbackPrice = typeof (ci as { price?: number }).price === 'number' ? (ci as { price?: number }).price! : 0;
          return {
            product: null as Product | null,
            unavailable: !catalogResolving,
            catalogPending: catalogResolving,
            lineId,
            productId: catalogProductId,
            quantity: ci.quantity,
            unitPrice: fallbackPrice,
            lineTotal: fallbackPrice * ci.quantity,
            temperaturePreference: preference,
            isBeverage: false
          };
        }
        const unitPrice =
          typeof quotedPrices[lineId] === 'number'
            ? quotedPrices[lineId]
            : typeof quotedPrices[catalogProductId] === 'number'
              ? quotedPrices[catalogProductId]
              : Number(p.price || 0);
        return {
          product: p,
          unavailable: false as const,
          catalogPending: false as const,
          lineId,
          productId: catalogProductId,
          quantity: ci.quantity,
          unitPrice,
          lineTotal: unitPrice * ci.quantity,
          temperaturePreference: preference,
          isBeverage: isBeverageProduct(p)
        };
      })
      .filter(Boolean) as CartLineItem[];
  }, [core.cart, core.products, core.isBootstrapping, core.productsFetching, quotedPrices]);

  const unavailableLineItems = useMemo(
    () => lineItems.filter((li) => li.unavailable && !li.catalogPending),
    [lineItems]
  );
  const availableLineItems = useMemo(
    () => lineItems.filter((li) => !li.unavailable && !li.catalogPending && li.product),
    [lineItems]
  );
  const catalogPendingLineItems = useMemo(
    () => lineItems.filter((li) => li.catalogPending),
    [lineItems]
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const openSeq = ++drawerOpenQuoteSeqRef.current;
    const beverageLines = (core.cart || [])
      .map(ci => {
        const preference = normalizeTemperaturePreference(ci.temperaturePreference);
        const catalogProductId = stripCartLineTempSuffix(
          String((ci as { productId?: string }).productId || ci.id || '').trim()
        );
        if (!catalogProductId) return null;
        const p = (core.products || []).find(
          prod =>
            prod.frontendId === catalogProductId ||
            prod.id === catalogProductId ||
            (prod as { _id?: string })._id === catalogProductId
        );
        if (!p || !isBeverageProduct(p)) return null;
        return {
          lineId: cartLineId(catalogProductId, preference),
          productId: catalogProductId,
          temperaturePreference: preference
        };
      })
      .filter(Boolean) as Array<{
      lineId: string;
      productId: string;
      temperaturePreference: TemperaturePreference;
    }>;

    if (beverageLines.length === 0) return;

    void (async () => {
      const next: Record<string, number> = {};
      await Promise.all(
        beverageLines.map(async li => {
          try {
            const quote = await productsApi.temperatureQuote(li.productId, li.temperaturePreference);
            if (typeof quote?.unitPrice === 'number' && Number.isFinite(quote.unitPrice)) {
              next[li.lineId] = quote.unitPrice;
            }
          } catch {
            // Keep catalog price on quote failure.
          }
        })
      );
      if (!cancelled && openSeq === drawerOpenQuoteSeqRef.current) {
        setQuotedPrices(prev => ({ ...prev, ...next }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, core.cart, core.products]);

  const subtotal = useMemo(
    () => availableLineItems.reduce((sum, li) => sum + li.lineTotal, 0),
    [availableLineItems]
  );

  const handleRemoveItem = (lineId: string) => {
    const target = String(lineId || '').trim();
    core.setCart(prev => prev.filter(i => String(i.id || '').trim() !== target));
  };

  const handleUpdateQuantity = (lineId: string, nextQty: number, maxStock?: number) => {
    const target = String(lineId || '').trim();
    if (nextQty <= 0) {
      core.setCart(prev => prev.filter(i => String(i.id || '').trim() !== target));
      return;
    }
    if (typeof maxStock === 'number' && maxStock > 0 && nextQty > maxStock) {
      core.addToast(`Max stock reached (${maxStock})`, 'warning');
      return;
    }
    if (nextQty > CART_MAX_QUANTITY_PER_ITEM) {
      core.addToast(`You can order up to ${CART_MAX_QUANTITY_PER_ITEM} of one item per order`, 'warning');
      return;
    }
    core.setCart(prev =>
      prev.map(i => (String(i.id || '').trim() === target ? { ...i, quantity: nextQty } : i))
    );
  };

  const handleRemoveOutOfStockItems = () => {
    const oosLineIds = new Set(
      availableLineItems
        .filter(li => li.product && (li.product.stock ?? 0) <= 0)
        .map(li => li.lineId)
    );
    core.setCart(prev => prev.filter(i => !oosLineIds.has(String(i.id || '').trim())));
    core.addToast('Removed out of stock items', 'info');
  };

  const handleRemoveUnavailableItems = () => {
    const ids = new Set(unavailableLineItems.map((li) => li.lineId));
    core.setCart(prev => prev.filter(i => !ids.has(String(i.id || '').trim())));
    core.addToast('Removed unavailable items', 'info');
  };

  const handleClearAllItems = async () => {
    core.setCart([]);
    if (isAuthenticated) {
      try {
        await cartApi.clear();
      } catch {
        // Local state cleared
      }
    }
    core.addToast('Cart cleared', 'info');
  };

  const hasOutOfStockItems = useMemo(
    () => availableLineItems.some(li => li.product && (li.product.stock ?? 0) <= 0),
    [availableLineItems]
  );
  const hasUnavailableItems = unavailableLineItems.length > 0;

  const handleTemperatureChange = async (lineId: string, next: TemperaturePreference) => {
    const sourceLineId = String(lineId || '').trim();
    const source = (core.cart || []).find((i) => String(i.id || '').trim() === sourceLineId);
    if (!source) return;
    const productId = stripCartLineTempSuffix(
      String((source as { productId?: string }).productId || source.id || '').trim()
    );
    if (!productId) return;
    const currentPref = normalizeTemperaturePreference(source.temperaturePreference);
    if (currentPref === next) return;

    const targetLineId = cartLineId(productId, next);
    const seq = (tempQuoteSeqByProductRef.current[targetLineId] || 0) + 1;
    tempQuoteSeqByProductRef.current[targetLineId] = seq;

    // Move / merge into the destination temp line (never leave two keys for the same SKU+temp).
    core.setCart((prev) => {
      const from = prev.find((i) => String(i.id || '').trim() === sourceLineId);
      if (!from) return prev;
      const qty = Math.max(0, Number(from.quantity || 0));
      const withoutSource = prev.filter((i) => String(i.id || '').trim() !== sourceLineId);
      const destIdx = withoutSource.findIndex((i) => String(i.id || '').trim() === targetLineId);
      if (destIdx >= 0) {
        const nextRows = [...withoutSource];
        nextRows[destIdx] = {
          ...nextRows[destIdx],
          id: targetLineId,
          productId,
          quantity: Math.min(CART_MAX_QUANTITY_PER_ITEM, nextRows[destIdx].quantity + qty),
          temperaturePreference: next
        };
        return nextRows;
      }
      return [
        ...withoutSource,
        {
          ...from,
          id: targetLineId,
          productId,
          quantity: qty,
          temperaturePreference: next
        }
      ];
    });

    if (next === 'chilled') {
      try {
        const quote = await productsApi.temperatureQuote(productId, 'chilled');
        if (seq !== tempQuoteSeqByProductRef.current[targetLineId]) return;
        if (quote?.coldUnavailable || quote?.chilledUnavailable) {
          core.addToast('Chilled delivery unavailable nearby — defaulting to ambient price', 'warning');
          const ambientLineId = cartLineId(productId, 'ambient');
          core.setCart((prev) => {
            const from = prev.find((i) => String(i.id || '').trim() === targetLineId);
            if (!from) return prev;
            const qty = Math.max(0, Number(from.quantity || 0));
            const without = prev.filter((i) => String(i.id || '').trim() !== targetLineId);
            const destIdx = without.findIndex((i) => String(i.id || '').trim() === ambientLineId);
            if (destIdx >= 0) {
              const nextRows = [...without];
              nextRows[destIdx] = {
                ...nextRows[destIdx],
                id: ambientLineId,
                productId,
                quantity: Math.min(CART_MAX_QUANTITY_PER_ITEM, nextRows[destIdx].quantity + qty),
                temperaturePreference: 'ambient'
              };
              return nextRows;
            }
            return [
              ...without,
              { ...from, id: ambientLineId, productId, quantity: qty, temperaturePreference: 'ambient' }
            ];
          });
          const ambientQuote = await productsApi.temperatureQuote(productId, 'ambient');
          if (seq !== tempQuoteSeqByProductRef.current[targetLineId]) return;
          if (typeof ambientQuote?.unitPrice === 'number') {
            setQuotedPrices(prev => ({ ...prev, [ambientLineId]: ambientQuote.unitPrice as number }));
          }
          return;
        }
        if (typeof quote?.unitPrice === 'number') {
          setQuotedPrices(prev => ({ ...prev, [targetLineId]: quote.unitPrice as number }));
        }
      } catch {
        if (seq !== tempQuoteSeqByProductRef.current[targetLineId]) return;
        return;
      }
    } else {
      try {
        const quote = await productsApi.temperatureQuote(productId, 'ambient');
        if (seq !== tempQuoteSeqByProductRef.current[targetLineId]) return;
        if (typeof quote?.unitPrice === 'number') {
          setQuotedPrices(prev => ({ ...prev, [targetLineId]: quote.unitPrice as number }));
        }
      } catch {
        // Keep existing price.
      }
    }
  };

  const handleCheckoutClick = () => {
    const maintenanceMode = Boolean(core.settings?.maintenanceMode);
    const checkoutAvailable = core.settings?.checkoutAvailable !== false;
    if (maintenanceMode || !checkoutAvailable) {
      return;
    }
    onClose();
    navigate('/checkout');
  };

  useEffect(() => {
    if (!isOpen) return;
    // Seed count when drawer opens; live updates come from CustomerReturnScanner onChange.
    try {
      const stored = localStorage.getItem('ninpo_return_upcs_v1');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setBottleReturnsCount(
            parsed.reduce((sum, item: { quantity?: number }) => sum + (Number(item?.quantity) || 0), 0)
          );
          return;
        }
      }
    } catch {
      // ignore malformed local storage
    }
    setBottleReturnsCount(0);
  }, [isOpen]);

  const maintenanceMode = Boolean(core.settings?.maintenanceMode);
  const checkoutAvailable = core.settings?.checkoutAvailable !== false;
  const checkoutBlockedBySettings = maintenanceMode || !checkoutAvailable;

  const requiresAgeVerification = useMemo(() => {
    return lineItems.some(
      item => item.product?.requiresAgeVerification || item.product?.isAgeRestricted
    );
  }, [lineItems]);

  const isCheckoutDisabled = useMemo(() => {
    if (checkoutBlockedBySettings) return true;
    if (catalogPendingLineItems.length > 0) return true;
    if (hasOutOfStockItems || hasUnavailableItems) return true;
    if (availableLineItems.length > 0) return false;
    if (bottleReturnsCount >= 100) return false;
    return true;
  }, [
    checkoutBlockedBySettings,
    catalogPendingLineItems.length,
    hasOutOfStockItems,
    hasUnavailableItems,
    availableLineItems.length,
    bottleReturnsCount
  ]);

  const uniqueStoreCount = useMemo(() => {
    const stores = new Set<string>();
    for (const item of availableLineItems) {
      const storeId = item.product?.pricingStoreId || item.product?.storePrices?.[0]?.storeId;
      if (storeId) stores.add(storeId);
    }
    return Math.max(1, stores.size);
  }, [availableLineItems]);

  const checkoutButtonText = useMemo(() => {
    if (maintenanceMode) return 'Checkout unavailable — maintenance';
    if (!checkoutAvailable) return 'Checkout temporarily unavailable';
    if (catalogPendingLineItems.length > 0) return 'Matching cart to catalog…';
    if (hasUnavailableItems) return 'Remove unavailable items to checkout';
    if (hasOutOfStockItems) return 'Remove Out-of-Stock Items to Checkout';
    if (availableLineItems.length > 0 && bottleReturnsCount > 0) {
      return 'Checkout order (returns included)';
    }
    if (availableLineItems.length > 0 && uncountedBagAttached) {
      return 'Checkout order (uncounted bag)';
    }
    if (availableLineItems.length > 0) return 'Proceed to Secure Checkout';
    if (bottleReturnsCount >= 100) return 'Place return-only pickup';
    if (bottleReturnsCount > 0) {
      return `Need ${100 - bottleReturnsCount} more returns (or add groceries)`;
    }
    if (uncountedBagAttached) {
      return 'Add groceries to checkout with uncounted bag';
    }
    return 'Proceed to Secure Checkout';
  }, [
    maintenanceMode,
    checkoutAvailable,
    catalogPendingLineItems.length,
    hasUnavailableItems,
    hasOutOfStockItems,
    availableLineItems.length,
    bottleReturnsCount,
    uncountedBagAttached
  ]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Overlay */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close cart drawer"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity opacity-100 animate-fadeIn"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') onClose(); }}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        tabIndex={-1}
        className="absolute right-0 top-0 h-full w-full max-w-lg bg-ninpo-black border-l border-white/5 shadow-2xl transition-transform translate-x-0 animate-slideInRight outline-none"
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div
            className="p-6 border-b border-white/5 flex items-center justify-between"
            style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top, 0px))' }}
          >
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-5 h-5 text-ninpo-lime" />
              <div>
                <p className="text-white font-black uppercase tracking-widest text-sm">Cart</p>
                <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest mt-1">
                  Preview your items
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close cart drawer"
              className="p-3 rounded-2xl bg-ninpo-red/10 text-ninpo-red border border-ninpo-red/20 hover:bg-ninpo-red/20 transition-all duration-300"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <FreeDeliveryProgressBar subtotal={subtotal} />

            <CartReturnsSection
              isScannerOpen={isScannerOpen}
              onToggleScanner={() => setIsScannerOpen(!isScannerOpen)}
              hasCartItems={lineItems.length > 0}
              isAuthenticated={isAuthenticated}
              initialReturnUpcs={initialReturnUpcs}
              initialEligibilityCache={initialEligibilityCache}
              onStatePersist={persistReturnUpcs}
              onChange={(totalContainers, estimatedCredit) => {
                setBottleReturnsCount(totalContainers);
                setBottleReturnsCredit(estimatedCredit);
              }}
              onUncountedBagChange={setUncountedBagAttached}
              onComplete={() => setIsScannerOpen(false)}
              returnSyncError={returnSyncError}
            />

            <CartLineList
              catalogPendingLineItems={catalogPendingLineItems}
              unavailableLineItems={unavailableLineItems}
              availableLineItems={availableLineItems}
              hasOutOfStockItems={hasOutOfStockItems}
              hasUnavailableItems={hasUnavailableItems}
              suggestedProducts={suggestedProducts}
              onRemoveOutOfStockItems={handleRemoveOutOfStockItems}
              onRemoveUnavailableItems={handleRemoveUnavailableItems}
              onClearAllItems={handleClearAllItems}
              onRemoveItem={handleRemoveItem}
              onUpdateQuantity={handleUpdateQuantity}
              onTemperatureChange={(lineId, next) => {
                void handleTemperatureChange(lineId, next);
              }}
              onAddSuggestion={handleAddSuggestion}
            />

            {isAuthenticated && (
              <div className="pt-4 border-t border-white/5">
                <BuyAgainCarousel className="w-full" />
              </div>
            )}
          </div>

          <CartDrawerFooter
            subtotal={subtotal}
            bottleReturnsCount={bottleReturnsCount}
            bottleReturnsCredit={bottleReturnsCredit}
            uncountedBagAttached={uncountedBagAttached}
            uniqueStoreCount={uniqueStoreCount}
            lineItemCount={lineItems.length}
            isCheckoutDisabled={isCheckoutDisabled}
            checkoutButtonText={checkoutButtonText}
            onCheckoutClick={handleCheckoutClick}
            tier={core.currentUser?.tier || core.currentUser?.membershipTier}
            requiresAgeVerification={requiresAgeVerification}
          />
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
